import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import {
  leagueCustomerMembershipSettingsRoute,
  leagueCustomerMembershipsEnrollMissingContinueRoute,
  leagueCustomerMembershipsEnrollMissingRoute,
} from "network/api-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import { isRevaAdminUser, membershipCurrencySymbol, membershipUsesAnnualFee } from "utils/membership-ui-helpers";
import {
  MEMBER_NUMBER_DEFAULTS,
  memberNumberSettingsChanged,
  memberNumberSettingsFromApi,
  prefixSequenceExample,
} from "./member-number-settings";

const currentYearJanuary = () => `${new Date().getFullYear()}-01`;

const amountsConfigured = (form) => {
  const monthly = Number(form.membership_monthly_amount);
  const yearly = Number(form.membership_yearly_amount);
  return (Number.isFinite(monthly) && monthly > 0) || (Number.isFinite(yearly) && yearly > 0);
};

const MetricCard = ({ label, value }) => (
  <Card variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
    <CardContent>
      <RevaText variant="overline" color="text.secondary">
        {label}
      </RevaText>
      <RevaText variant="h4" sx={{ mt: 0.5 }}>
        {value}
      </RevaText>
    </CardContent>
  </Card>
);

const initialForm = {
  membership_enforcement: "soft",
  membership_grace_days: 7,
  membership_lifetime_years: "",
  require_membership_to_play: false,
  membership_monthly_amount: "",
  membership_yearly_amount: "",
  member_number_settings: MEMBER_NUMBER_DEFAULTS,
};

const GestionMembershipSettings = ({ embedded = false, accountSelector = null }) => {
  const intl = useIntl();
  const { customerId } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [resumeBlocked, setResumeBlocked] = useState(false);
  const [currencyCode, setCurrencyCode] = useState(null);
  const [withMembership, setWithMembership] = useState(0);
  const [withoutMembership, setWithoutMembership] = useState(0);
  const [enrollmentRun, setEnrollmentRun] = useState(null);
  const [backfill, setBackfill] = useState(false);
  const [startMonth, setStartMonth] = useState(currentYearJanuary);
  const [form, setForm] = useState(initialForm);
  const [savedForm, setSavedForm] = useState(initialForm);
  const [useAnnual, setUseAnnual] = useState(false);
  const [preview, setPreview] = useState(null);
  const isRevaAdmin = isRevaAdminUser(user);

  const applySettingsResponse = useCallback((data = {}) => {
    const nextForm = {
      membership_enforcement: data.membership_enforcement || "soft",
      membership_grace_days: data.membership_grace_days ?? 7,
      membership_lifetime_years: data.membership_lifetime_years ?? "",
      require_membership_to_play: Boolean(data.require_membership_to_play),
      membership_monthly_amount: data.membership_monthly_amount ?? "",
      membership_yearly_amount: data.membership_yearly_amount ?? "",
      member_number_settings: memberNumberSettingsFromApi(data),
    };
    setCurrencyCode(data.currency_code || null);
    setWithMembership(Number(data.roster_members_with_membership || 0));
    setWithoutMembership(Number(data.roster_members_without_membership || 0));
    setEnrollmentRun(data.membership_enrollment_run || null);
    setPreview(data.member_number_preview || null);
    setForm(nextForm);
    setSavedForm(nextForm);
    setUseAnnual(membershipUsesAnnualFee(nextForm));
  }, []);

  const loadSettings = useCallback(() => {
    if (!customerId) return;
    setLoading(true);
    return performRequest("get", leagueCustomerMembershipSettingsRoute(customerId), {}, true, user)
      .then((res) => applySettingsResponse(res.data || {}))
      .catch((error) => enqueueSnackbar(error?.response?.data?.message || "Error al cargar ajustes", { variant: "error" }))
      .finally(() => setLoading(false));
  }, [applySettingsResponse, customerId, enqueueSnackbar, user]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = () => {
    setSaving(true);
    performRequest(
      "patch",
      leagueCustomerMembershipSettingsRoute(customerId),
      {
        ...form,
        membership_lifetime_years: form.membership_lifetime_years === "" ? null : Number(form.membership_lifetime_years),
        membership_monthly_amount: useAnnual
          ? null
          : form.membership_monthly_amount === ""
            ? null
            : Number(form.membership_monthly_amount),
        membership_yearly_amount: useAnnual
          ? form.membership_yearly_amount === ""
            ? null
            : Number(form.membership_yearly_amount)
          : null,
      },
      true,
      user,
    )
      .then((res) => {
        applySettingsResponse(res.data || {});
        enqueueSnackbar("Ajustes guardados", { variant: "success" });
      })
      .catch((error) => enqueueSnackbar(error?.response?.data?.message || "Error al guardar", { variant: "error" }))
      .finally(() => setSaving(false));
  };

  const firstDebtMonth = backfill ? startMonth : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const hasUnsavedChanges =
    JSON.stringify(form) !== JSON.stringify(savedForm) || useAnnual !== membershipUsesAnnualFee(savedForm);
  const memberNumbersDirty = memberNumberSettingsChanged(
    savedForm.member_number_settings,
    form.member_number_settings,
  );
  const runIsActive = enrollmentRun?.status === "running";
  const canEnroll = amountsConfigured(savedForm) && withoutMembership > 0 && !hasUnsavedChanges && !runIsActive;

  const processRemainingBatches = useCallback(
    async (initialRun) => {
      let currentRun = initialRun;
      while (currentRun?.status === "running") {
        const response = await performRequest(
          "post",
          leagueCustomerMembershipsEnrollMissingContinueRoute(customerId, currentRun.id),
          {},
          true,
          user,
        );
        currentRun = response.data || {};
        setEnrollmentRun(currentRun);
      }

      return currentRun;
    },
    [customerId, user],
  );

  const continueEnrollment = useCallback(
    async (run) => {
      setEnrolling(true);
      setResumeBlocked(false);
      try {
        const completedRun = await processRemainingBatches(run);
        enqueueSnackbar(
          intl.formatMessage(
            { id: "gestion.membership.enrollMissingSuccess" },
            { created: completedRun?.created ?? 0, month: completedRun?.start_month || firstDebtMonth },
          ),
          { variant: completedRun?.failed > 0 ? "warning" : "success" },
        );
        await loadSettings();
      } catch (error) {
        setResumeBlocked(true);
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "gestion.membership.enrollMissingError" }),
          { variant: "error" },
        );
        await loadSettings();
      } finally {
        setEnrolling(false);
      }
    },
    [enqueueSnackbar, firstDebtMonth, intl, loadSettings, processRemainingBatches],
  );

  useEffect(() => {
    if (isRevaAdmin && runIsActive && !enrolling && !resumeBlocked) {
      continueEnrollment(enrollmentRun);
    }
  }, [continueEnrollment, enrolling, enrollmentRun, isRevaAdmin, resumeBlocked, runIsActive]);

  const handleFeePeriodChange = (checked) => {
    setUseAnnual(checked);
    setForm((current) => ({
      ...current,
      membership_monthly_amount: checked ? "" : current.membership_monthly_amount,
      membership_yearly_amount: checked ? current.membership_yearly_amount : "",
    }));
  };

  const handleEnrollMissing = async () => {
    if (!customerId || !canEnroll) return;
    if (backfill && !startMonth) return;
    const confirmed = window.confirm(
      intl.formatMessage({ id: "gestion.membership.enrollMissingConfirm" }, { count: withoutMembership, month: firstDebtMonth }),
    );
    if (!confirmed) return;

    setEnrolling(true);
    setResumeBlocked(false);
    try {
      const response = await performRequest(
        "post",
        leagueCustomerMembershipsEnrollMissingRoute(customerId),
        backfill ? { backfill: true, start_month: startMonth } : { backfill: false },
        true,
        user,
      );
      const completedRun = await processRemainingBatches(response.data || {});
      setEnrollmentRun(completedRun);
      enqueueSnackbar(
        intl.formatMessage(
          { id: "gestion.membership.enrollMissingSuccess" },
          { created: completedRun?.created ?? 0, month: completedRun?.start_month || firstDebtMonth },
        ),
        { variant: completedRun?.failed > 0 ? "warning" : "success" },
      );
      await loadSettings();
    } catch (error) {
      setResumeBlocked(true);
      enqueueSnackbar(
        error?.response?.data?.message || intl.formatMessage({ id: "gestion.membership.enrollMissingError" }),
        { variant: "error" },
      );
      await loadSettings();
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) return <CircularProgress />;

  const currencySymbol = membershipCurrencySymbol(currencyCode);
  const amountAdornment = currencySymbol
    ? { startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }
    : undefined;
  const processed = Number(enrollmentRun?.processed || 0);
  const total = Number(enrollmentRun?.total || 0);
  const progress = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 100;

  return (
    <Box>
      {!embedded && (
        <RevaText variant="h5" sx={{ mb: 2 }}>
          Ajustes de socios
        </RevaText>
      )}
      <Stack spacing={2}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: accountSelector ? { xs: "1fr", md: "320px minmax(0, 1fr)" } : "minmax(0, 760px)",
            gap: 2,
            alignItems: "stretch",
          }}
        >
          {accountSelector}
          <Card variant="outlined">
            <CardContent>
              <RevaText variant="overline" color="text.secondary">
                {intl.formatMessage({ id: "gestion.membership.settingsTitle" })}
              </RevaText>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
                  gap: 1.5,
                  mt: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Control en planillero"
                  value={form.membership_enforcement}
                  onChange={(event) => setForm({ ...form, membership_enforcement: event.target.value })}
                >
                  <MenuItem value="soft">Suave (avisar)</MenuItem>
                  <MenuItem value="hard">Estricto (bloquear check-in)</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label="Días de gracia"
                  value={form.membership_grace_days}
                  onChange={(event) => setForm({ ...form, membership_grace_days: Number(event.target.value) })}
                />
                <TextField
                  size="small"
                  type="number"
                  label={intl.formatMessage({ id: "gestion.membership.lifetimeYears" })}
                  helperText={intl.formatMessage({ id: "gestion.membership.lifetimeYearsHelp" })}
                  value={form.membership_lifetime_years}
                  inputProps={{ min: 1, max: 100 }}
                  onChange={(event) => setForm({ ...form, membership_lifetime_years: event.target.value })}
                />
                <TextField
                  select
                  size="small"
                  label="Requiere membresía para jugar"
                  value={form.require_membership_to_play ? "yes" : "no"}
                  onChange={(event) => setForm({ ...form, require_membership_to_play: event.target.value === "yes" })}
                >
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="yes">Sí</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label={intl.formatMessage({
                    id: useAnnual ? "gestion.membership.yearlyAmount" : "gestion.membership.monthlyAmount",
                  })}
                  value={
                    useAnnual ? (form.membership_yearly_amount ?? "") : (form.membership_monthly_amount ?? "")
                  }
                  onChange={(event) =>
                    setForm({
                      ...form,
                      [useAnnual ? "membership_yearly_amount" : "membership_monthly_amount"]: event.target.value,
                    })
                  }
                  helperText={currencyCode ? `Moneda: ${currencyCode}` : undefined}
                  InputProps={amountAdornment}
                />
                <FormControlLabel
                  control={
                    <Checkbox checked={useAnnual} onChange={(event) => handleFeePeriodChange(event.target.checked)} />
                  }
                  label={intl.formatMessage({ id: "gestion.membership.annualFee" })}
                />
                <Button variant="contained" onClick={handleSave} disabled={saving || enrolling || !hasUnsavedChanges}>
                  {saving ? <CircularProgress size={18} /> : intl.formatMessage({ id: "gestion.membership.saveSettings" })}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Card variant="outlined">
          <CardContent>
            <RevaText variant="overline" color="text.secondary">
              {intl.formatMessage({ id: "gestion.membership.memberNumbersTitle" })}
            </RevaText>
            <RevaText variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
              {intl.formatMessage({ id: "gestion.membership.memberNumbersHint" })}
            </RevaText>
            <Stack spacing={1.5} sx={{ maxWidth: 560 }}>
              <RadioGroup
                row
                value={form.member_number_settings.mode}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    member_number_settings: { ...current.member_number_settings, mode: event.target.value },
                  }))
                }
              >
                <FormControlLabel
                  value="automatic"
                  control={<Radio />}
                  label={intl.formatMessage({ id: "gestion.membership.memberNumbersAutomatic" })}
                />
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label={intl.formatMessage({ id: "gestion.membership.memberNumbersManual" })}
                />
              </RadioGroup>
              {form.member_number_settings.mode === "automatic" ? (
                <>
                  <TextField
                    select
                    size="small"
                    label={intl.formatMessage({ id: "gestion.membership.memberNumbersFormat" })}
                    value={form.member_number_settings.format}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        member_number_settings: { ...current.member_number_settings, format: event.target.value },
                      }))
                    }
                  >
                    <MenuItem value="prefix_sequential">
                      {intl.formatMessage(
                        { id: "gestion.membership.memberNumbersFormatPrefix" },
                        { example: prefixSequenceExample(form.member_number_settings) },
                      )}
                    </MenuItem>
                    <MenuItem value="sequential">
                      {intl.formatMessage({ id: "gestion.membership.memberNumbersFormatSequential" })}
                    </MenuItem>
                    <MenuItem value="sequential_extension">
                      {intl.formatMessage({ id: "gestion.membership.memberNumbersFormatFamily" })}
                    </MenuItem>
                    <MenuItem value="alphanumeric">
                      {intl.formatMessage({ id: "gestion.membership.memberNumbersFormatAlpha" })}
                    </MenuItem>
                  </TextField>
                  {form.member_number_settings.format === "prefix_sequential" ? (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <TextField
                        size="small"
                        label={intl.formatMessage({ id: "gestion.membership.memberNumbersPrefix" })}
                        value={form.member_number_settings.prefix}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            member_number_settings: { ...current.member_number_settings, prefix: event.target.value },
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={intl.formatMessage({ id: "gestion.membership.memberNumbersPadding" })}
                        value={form.member_number_settings.padding}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            member_number_settings: {
                              ...current.member_number_settings,
                              padding: Number(event.target.value),
                            },
                          }))
                        }
                      />
                    </Stack>
                  ) : null}
                  {form.member_number_settings.format === "alphanumeric" ? (
                    <TextField
                      size="small"
                      type="number"
                      label={intl.formatMessage({ id: "gestion.membership.memberNumbersLength" })}
                      value={form.member_number_settings.alphanumeric_length}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          member_number_settings: {
                            ...current.member_number_settings,
                            alphanumeric_length: Number(event.target.value),
                          },
                        }))
                      }
                    />
                  ) : null}
                  <Alert severity={memberNumbersDirty ? "warning" : "info"}>
                    {intl.formatMessage(
                      {
                        id: memberNumbersDirty
                          ? "gestion.membership.memberNumbersPreviewStale"
                          : "gestion.membership.memberNumbersPreview",
                      },
                      { value: preview || "—" },
                    )}
                  </Alert>
                </>
              ) : (
                <Alert severity="info">{intl.formatMessage({ id: "gestion.membership.memberNumbersManualHint" })}</Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        {isRevaAdmin ? (
          <Accordion defaultExpanded={false} variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <RevaText variant="subtitle1">
                {intl.formatMessage({ id: "gestion.membership.enrollMissing" })}
              </RevaText>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <MetricCard
                    label={intl.formatMessage({ id: "gestion.membership.affiliatedCount" })}
                    value={withMembership}
                  />
                  <MetricCard
                    label={intl.formatMessage({ id: "gestion.membership.unaffiliatedCount" })}
                    value={withoutMembership}
                  />
                </Stack>
                <RevaText variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: "gestion.membership.enrollMissingHint" })}
                </RevaText>
                <RevaText variant="body2">
                  {hasUnsavedChanges
                    ? intl.formatMessage({ id: "gestion.membership.enrollMissingUnsaved" })
                    : !amountsConfigured(savedForm)
                      ? intl.formatMessage({ id: "gestion.membership.enrollMissingNeedAmount" })
                      : withoutMembership > 0
                        ? intl.formatMessage({ id: "gestion.membership.enrollMissingCount" }, { count: withoutMembership })
                        : intl.formatMessage({ id: "gestion.membership.enrollMissingNone" })}
                </RevaText>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                  <FormControlLabel
                    control={<Checkbox checked={backfill} onChange={(event) => setBackfill(event.target.checked)} />}
                    label={intl.formatMessage({ id: "gestion.membership.enrollMissingBackfill" })}
                    disabled={enrolling || runIsActive}
                  />
                  {backfill ? (
                    <TextField
                      type="month"
                      size="small"
                      label={intl.formatMessage({ id: "gestion.membership.enrollMissingStartMonth" })}
                      value={startMonth}
                      onChange={(event) => setStartMonth(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={enrolling || runIsActive}
                    />
                  ) : null}
                  <Button
                    variant="outlined"
                    onClick={handleEnrollMissing}
                    disabled={!canEnroll || enrolling || (backfill && !startMonth)}
                  >
                    {enrolling ? <CircularProgress size={18} /> : intl.formatMessage({ id: "gestion.membership.enrollMissing" })}
                  </Button>
                </Stack>

                {enrollmentRun ? (
                  <Alert
                    severity={
                      enrollmentRun.status === "running"
                        ? "info"
                        : Number(enrollmentRun.failed || 0) > 0
                          ? "warning"
                          : "success"
                    }
                    action={
                      enrollmentRun.status === "running" && resumeBlocked ? (
                        <Button color="inherit" size="small" onClick={() => continueEnrollment(enrollmentRun)}>
                          {intl.formatMessage({ id: "gestion.membership.resumeEnrollment" })}
                        </Button>
                      ) : undefined
                    }
                  >
                    <RevaText variant="body2" sx={{ fontWeight: 600 }}>
                      {enrollmentRun.status === "running"
                        ? intl.formatMessage(
                            { id: "gestion.membership.enrollmentProgress" },
                            { processed, total },
                          )
                        : intl.formatMessage(
                            { id: "gestion.membership.enrollmentSummary" },
                            {
                              created: enrollmentRun.created || 0,
                              skipped: enrollmentRun.skipped || 0,
                              failed: enrollmentRun.failed || 0,
                            },
                          )}
                    </RevaText>
                    {enrollmentRun.status === "running" ? (
                      <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />
                    ) : null}
                  </Alert>
                ) : null}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ) : null}
      </Stack>
    </Box>
  );
};

export default GestionMembershipSettings;
